import { IVector3 } from '../api.js';
export class IPlane {
    constructor(...args // 4 args
    ) {
        if (args.length === 3) {
            const [p1, p2, p3] = args;
            const normal = IVector3.normalize_in_place(IVector3.cross(IVector3.subtract(p2, p1), IVector3.subtract(p3, p1)));
            return {
                normal: normal,
                d: -IVector3.dot(normal, p1)
            };
        }
        else if (args.length === 2) {
            // construct from normal + point
            const [normal, point] = args;
            return {
                normal: IVector3.copy(normal),
                d: -IVector3.dot(normal, point)
            };
        }
        else if (args.length === 4) {
            return {
                normal: new IVector3(args[0], args[1], args[2]),
                d: args[3]
            };
        }
        else {
            throw Error('Failed to construct plane from given parameters');
        }
    }
    static copy(plane) {
        return new IPlane(plane.normal.x, plane.normal.y, plane.normal.z, plane.d);
    }
    static copy_to_ref(plane, ref) {
        ref.normal.x = plane.normal.x;
        ref.normal.y = plane.normal.y;
        ref.normal.z = plane.normal.z;
        ref.d = plane.d;
        return ref;
    }
    static set(plane, p1, p2, p3) {
        const x1 = p2.x - p1.x;
        const y1 = p2.y - p1.y;
        const z1 = p2.z - p1.z;
        const x2 = p3.x - p1.x;
        const y2 = p3.y - p1.y;
        const z2 = p3.z - p1.z;
        plane.normal.x = y1 * z2 - z1 * y2;
        plane.normal.y = z1 * x2 - x1 * z2;
        plane.normal.z = x1 * y2 - y1 * x2;
        IVector3.normalize_in_place(plane.normal);
        plane.d = -IVector3.dot(plane.normal, p1);
        return plane;
    }
    /**
     * Checks if the entire sphere is on the positive side of the plane
     * where the positive side is the side that the plane normal is pointing towards
     * @param plane the plane to check against
     * @param sphere the sphere to check against
     * @returns true of the entire sphere is on the side of the plane the normal is pointing towards, false otherwise
     */
    static sphere_above_plane(plane, sphere) {
        const distance = IPlane.point_signed_distance(plane, sphere.position);
        return distance > sphere.radius;
    }
    /**
     * Checks if a point is on the positive side of the plane
     * where the positive side is the side that the plane normal is pointing towards
     * @param plane the plane to check against
     * @param point the point to check against
     * @returns 1 if on positive side, -1 if on negative side, and 0 if point is on plane
     */
    static point_above_plane(plane, point) {
        return Math.sign(IVector3.dot(point, plane.normal) + plane.d);
    }
    /**
     * calculate the signed distance from the plane to the point
     * the distance is positive if the point is on the side of the plane that the normal is pointing towards
     * @param plane the plane to check against
     * @param point the point to check against
     * @returns the signed distance between point and plane
     */
    static point_signed_distance(plane, point) {
        const normal_distance = IVector3.dot(point, plane.normal) + plane.d;
        return normal_distance / IVector3.magnitude(plane.normal);
    }
    static project_point(plane, point) {
        return IPlane.project_point_to_ref(plane, point, new IVector3());
    }
    static project_point_to_ref(plane, point, ref) {
        const normal_distance = IVector3.dot(point, plane.normal) + plane.d;
        return IVector3.subtract_to_ref(point, IVector3.scale(plane.normal, normal_distance), ref);
    }
    /**
     * Finds the intersecton point between 3 planes were no plane is parallel to another.
     * Note that this function will return an arbitrary point if one or more of the provided planes are parallel
     * @param p1 plane to find intersection for
     * @param p2 plane to find intersection for
     * @param p3 plane to find intersection for
     * @returns the point where all 3 planes intersect
     */
    static intersection_corner(p1, p2, p3) {
        return IPlane.intersection_corner_to_ref(p1, p2, p3, new IVector3());
    }
    /**
     * Finds the intersecton point between 3 planes were no plane is parallel to another.
     * Note that this function will return an arbitrary point if one or more of the provided planes are parallel
     * @param p1 plane to find intersection for
     * @param p2 plane to find intersection for
     * @param p3 plane to find intersection for
     * @param ref object to store intersection point in
     * @returns the point where all 3 planes intersect, stored in ref
     */
    static intersection_corner_to_ref(p1, p2, p3, ref) {
        // Make sure that even if the ref has NaN, this still works
        IVector3.set(ref, 0, 0, 0);
        IPlane.project_point_to_ref(p1, ref, ref);
        IPlane.project_point_to_ref(p2, ref, ref);
        IPlane.project_point_to_ref(p3, ref, ref);
        return ref;
    }
}
//# sourceMappingURL=IPlane.js.map